import type { Guide } from "./types.js";
import { Lead, H2, P, Ul, Li, C, Code, Callout, Step } from "./primitives.js";

export const meta = {
  id: "dev-add-channel",
  title: "Adding a distribution channel",
  category: "For developers",
  lastUpdated: "2026-05-23",
  description: "Step-by-step recipe for adding a new distribution channel. Mirrors the pattern used for Telegram / FB / IG / WhatsApp / X / TikTok.",
  relatedGuides: ["dev-architecture", "dev-add-integration"],
  readTimeMin: 6,
} as const;

export function Body() {
  return (
    <>
      <Lead>
        Adding a new distribution channel is one of the most common extensions. The pattern is well-established — 5 files to touch, ~2-4 hours of work depending on the channel's auth complexity. This guide walks through the standard recipe.
      </Lead>

      <Callout kind="tip">
        Read the existing <C>keywords/src/distribution/telegram.ts</C> + <C>facebook.ts</C> + <C>x.ts</C> first to see three different auth flavours (bot token / Page Access Token / OAuth 1.0a). Pick the closest analogue to your target channel and start from that.
      </Callout>

      <H2>The 5 files to touch</H2>
      <Ul>
        <Li><strong>1.</strong> <C>keywords/src/distribution/&lt;channel&gt;.ts</C> — the orchestrator</Li>
        <Li><strong>2.</strong> <C>keywords/src/index.ts</C> — barrel export</Li>
        <Li><strong>3.</strong> <C>keywords/src/types.ts</C> — add to <C>DistributionChannel</C> enum</Li>
        <Li><strong>4.</strong> <C>apps/ui-server/src/integrations/&lt;channel&gt;.ts</C> — Credentials card + Test action + setup guide</Li>
        <Li><strong>5.</strong> <C>apps/ui-server/src/routes/pipeline-actions.ts</C> — route branch + body schema fields</Li>
        <Li><strong>6.</strong> <C>apps/ui/src/pages/pipeline/KeywordDetailPanel.tsx</C> — Distribute dropdown entry</Li>
        <Li><strong>7.</strong> <C>apps/ui/src/lib/api.ts</C> — extend the <C>distribute()</C> channel union</Li>
        <Li><strong>8.</strong> <C>apps/ui/src/pages/help/guides/distribute-article.tsx</C> — add a section for the new channel</Li>
      </Ul>

      <H2>Step-by-step</H2>

      <Step n={1} title="Write the orchestrator">
        <P>Skeleton:</P>
        <Code>{`import type { Article, KeywordRecord } from "../types.js";

export interface XYZDistributeOpts {
  keyword: KeywordRecord;
  article: Article;
  articleMarkdown: string;
  articleUrl: string;
  // ...channel-specific options...
  dryRun?: boolean;
}

export interface XYZDistributeResult {
  posted: boolean;
  // ...channel-specific result fields...
  warning?: string;
  error?: string;
  dryRun?: boolean;
}

export async function distributeToXyz(opts: XYZDistributeOpts): Promise<XYZDistributeResult> {
  // 1. Pick assets (hero.png, short.mp4, etc.)
  // 2. Build caption heuristically (no LLM call)
  // 3. If opts.dryRun, return the plan without hitting the API
  // 4. Validate creds from process.env
  // 5. Live send + return result with success/failure
}`}</Code>
        <P>Conventions you MUST follow:</P>
        <Ul>
          <Li><strong>Heuristic caption only</strong> — pull from <C>article.h1</C> + <C>firstSentences(articleMarkdown, budget)</C> + hashtags from <C>brief.semanticMustCover</C>. NO LLM call. See "Why distribute-time captions are heuristic" in PROJECT_STATE decisions.</Li>
          <Li><strong>Dry-run mode</strong> — every channel supports <C>dryRun: true</C> that previews payload without external side effects.</Li>
          <Li><strong>No throws</strong> — return errors via <C>result.error</C>. Route layer logs + responds; raw exceptions break the distributions table logging.</Li>
          <Li><strong>Reuse existing assets</strong> — <C>data/images/&lt;slug&gt;/hero.png</C>, <C>data/videos/&lt;slug&gt;/&#123;short,square,horizontal&#125;.mp4</C>. Don't generate new assets per-channel.</Li>
        </Ul>
      </Step>

      <Step n={2} title="Export from the keywords barrel">
        <P>In <C>keywords/src/index.ts</C>:</P>
        <Code>{`export { distributeToXyz, type XYZDistributeOpts, type XYZDistributeResult } from "./distribution/xyz.js";`}</Code>
        <P>Then add <C>"xyz"</C> to the <C>DistributionChannel</C> enum in <C>types.ts</C>:</P>
        <Code>{`export const DistributionChannel = z.enum([..., "xyz"]);`}</Code>
      </Step>

      <Step n={3} title="Build the Credentials card">
        <P>Create <C>apps/ui-server/src/integrations/xyz.ts</C>. The <C>Integration</C> shape:</P>
        <Code>{`export const xyzIntegration: Integration = {
  id: "xyz",
  name: "XYZ Channel",
  description: "...",
  category: "social" | "messaging" | "email" | "...",
  docsUrl: "https://...",
  fields: [
    { key: "XYZ_API_KEY", label: "API Key", required: true, sensitive: true },
    // ...other fields...
  ],
  test: async (values) => {
    // Cheap auth-check call. Returns { ok: true, summary } or { ok: false, error }.
  },
  setupGuide: {
    intro: "...",
    sections: [
      { title: "1. ...", steps: [{ text: "...", code: "...", link: { label, url } }] },
      // ...3-5 sections is the norm...
    ],
  },
};`}</Code>
        <P>Then register it in <C>apps/ui-server/src/integrations/registry.ts</C>:</P>
        <Code>{`import { xyzIntegration } from "./xyz.js";
export const INTEGRATIONS = [..., xyzIntegration];`}</Code>
      </Step>

      <Step n={4} title="Wire the route branch">
        <P>In <C>apps/ui-server/src/routes/pipeline-actions.ts</C>, add a branch to the channel switch:</P>
        <Code>{`if (req.params.channel === "xyz") {
  const startedAt = nowIso();
  try {
    const result = await distributeToXyz({
      keyword: r,
      article: r.article,
      articleMarkdown: r.articleMarkdown,
      articleUrl: body.url,
      // ...
      dryRun: body.dryRun === true,
    });
    if (result.dryRun) {
      return { ok: true, dryRun: true, summary: "...", ...result };
    }
    distributionRepo().log({
      keywordId: r.id, channel: "xyz", kind: "channel_post",
      status: result.posted ? "sent" : "failed",
      // ...
    });
    if (!result.posted) return reply.code(500).send({ ok: false, error: result.error });
    return { ok: true, summary: "...", ... };
  } catch (e) {
    distributionRepo().log({ ...status: "failed", error: (e as Error).message });
    return reply.code(500).send({ ok: false, error: (e as Error).message });
  }
}`}</Code>
        <P>Also add any channel-specific fields to the <C>DistributeBody</C> Zod schema at the top of the file.</P>
      </Step>

      <Step n={5} title="Add UI dropdown entries">
        <P>In <C>apps/ui/src/pages/pipeline/KeywordDetailPanel.tsx</C>, add a <C>fireXyz()</C> handler + dropdown entries (dry-run + one or more live modes). Extend the <C>distribute()</C> channel union in <C>apps/ui/src/lib/api.ts</C>.</P>
      </Step>

      <Step n={6} title="Document in the Help Center">
        <P>Add a section to <C>distribute-article.tsx</C> for the new channel + (per the help-center maintenance memory) bump that guide's <C>meta.lastUpdated</C>.</P>
      </Step>

      <Step n={7} title="Smoke test (dry-run first)">
        <P>Restart the server, then:</P>
        <Code>{`curl -sS -X POST "http://127.0.0.1:4400/api/pipeline/keywords/<id>/distribute/xyz" \\
  -H "Content-Type: application/json" \\
  -d '{"url":"https://alternabe.co.il/...","dryRun":true}'`}</Code>
        <P>Confirm the response shows the planned payload. Then enable real creds + try a live send.</P>
      </Step>

      <H2>OAuth flavours we've seen</H2>
      <Ul>
        <Li><strong>Bot token / API key</strong> (Telegram, SerpAPI, Brevo, DataForSEO, Firecrawl) — simplest. Single env var, no OAuth dance.</Li>
        <Li><strong>OAuth 2.0 user-context, long-lived</strong> (Meta Page Access Token, WhatsApp System User Token) — paste once, lasts ~60 days or forever via System User. No refresh-token rotation.</Li>
        <Li><strong>OAuth 1.0a</strong> (X) — 4 credentials, HMAC-SHA1 signing inline. ~80 lines of <C>node:crypto</C>. No refresh required.</Li>
        <Li><strong>OAuth 2.0 with refresh token</strong> (TikTok) — access token 24h, refresh token 365 days. Orchestrator auto-refreshes; route layer persists new tokens via the credentials store.</Li>
        <Li><strong>External OAuth via MCP</strong> (YouTube, via shmool-youtube-mcp) — when an external MCP already exists and handles OAuth. Spawn via stdio, call tools.</Li>
      </Ul>

      <H2>Common gotchas</H2>
      <Ul>
        <Li><strong>Hebrew + emoji + slice()</strong> — JS String.slice works in UTF-16 code units. Mid-emoji slicing leaves dangling surrogates. For Hebrew + emoji captions, use code-point-aware truncation if you need exact char counts.</Li>
        <Li><strong>Cred store sync</strong> — <C>DotenvCredentialsStore.write()</C> updates BOTH the .env file AND <C>process.env</C> so hot-swap works. If you skip this, the next call still sees old values until restart.</Li>
        <Li><strong>Channel-specific body field collisions</strong> — Brevo + WhatsApp both wanted <C>to</C> in their body schemas. We renamed WA's to <C>whatsappTo</C>. Watch for similar collisions when adding channels.</Li>
      </Ul>
    </>
  );
}

export const guide: Guide = { meta, Body };
